"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.bucketItemReduce = exports.bucketModifyItems = exports.disableComputeRatio = exports.applyRatioRule = exports.ComputeRatioRule = exports.bucketSetTotals = exports.bucketTotals = exports.bucketItems = exports.bucketMeasures = exports.bucketMeasure = exports.bucketMeasureIndex = exports.bucketAttributes = exports.bucketAttribute = exports.bucketAttributeIndex = exports.bucketIsEmpty = exports.newBucket = exports.isBucket = exports.idMatchBucket = exports.anyBucket = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var attribute_1 = require("../attribute");
var measure_1 = require("../measure");
var totals_1 = require("../base/totals");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var factory_1 = require("../measure/factory");
var isArray_1 = tslib_1.__importDefault(require("lodash/isArray"));
var identity_1 = tslib_1.__importDefault(require("lodash/identity"));
var findIndex_1 = tslib_1.__importDefault(require("lodash/findIndex"));
var intersection_1 = tslib_1.__importDefault(require("lodash/intersection"));
var json_stable_stringify_1 = tslib_1.__importDefault(require("json-stable-stringify"));
/**
 * This predicate evaluates true for any bucket.
 *
 * @public
 */
exports.anyBucket = function (_) { return true; };
/**
 * Factory function for predicates that will evaluate true if bucket's id is same as the provided id.
 *
 * @public
 */
exports.idMatchBucket = function (id) { return function (bucket) {
    return bucket.localIdentifier === id;
}; };
//
// Type guards
//
/**
 * Type-guard testing whether the provided object is an instance of {@link IBucket}.
 *
 * @param obj - object to test
 * @public
 */
function isBucket(obj) {
    return (!isEmpty_1.default(obj) &&
        obj.localIdentifier !== undefined &&
        obj.items !== undefined);
}
exports.isBucket = isBucket;
//
// Functions
//
var AGGREGATION_KEYS = ["Sum", "Count", "Avg", "Min", "Max", "Median", "Runsum"];
function getIdentifier(obj) {
    if (measure_1.isMeasure(obj)) {
        return measure_1.measureIdentifier(obj);
    }
    if (attribute_1.isAttribute(obj)) {
        return attribute_1.attributeIdentifier(obj);
    }
    return undefined;
}
function getAttributeDisplayFormIdentifiers(obj) {
    var _a;
    var result = [];
    for (var _i = 0, _b = Object.keys(obj); _i < _b.length; _i++) {
        var objKey = _b[_i];
        var identifier = getIdentifier(obj[objKey]);
        if (identifier) {
            result.push((_a = {},
                _a[objKey] = identifier,
                _a));
        }
    }
    return result;
}
/**
 * Creates a new bucket with the provided id and all the specified content.
 *
 * @param localId - bucket identifier
 * @param content - items to put into the bucket; attributes, measures and/or totals
 * @returns always new instance
 * @public
 */
function newBucket(localId) {
    var content = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        content[_i - 1] = arguments[_i];
    }
    ts_invariant_1.default(localId, "local identifier must be specified");
    var items = [];
    var totals = [];
    (content ? content : []).forEach(function (i) {
        if (!i) {
            return;
        }
        var contentErrorMessage = "Contents of a bucket must be either attribute, measure or total.";
        if (attribute_1.isAttribute(i) || measure_1.isMeasure(i)) {
            items.push(i);
        }
        else if (totals_1.isTotal(i)) {
            totals.push(i);
        }
        else if (isArray_1.default(i)) {
            ts_invariant_1.default(false, "newBucket called with an array of length " + i.length + " as one of the items for bucket " + localId + "." +
                "Please make sure that you are not trying to put an array of items into a bucket that only accepts single item.");
        }
        else if (typeof i === "object") {
            if (Object.keys(i).indexOf("Default") > -1) {
                var identifiers = getAttributeDisplayFormIdentifiers(i).map(function (identifier) {
                    var k = Object.keys(identifier)[0];
                    var value = identifier[k];
                    return k + ": " + value;
                });
                ts_invariant_1.default(false, contentErrorMessage + " It looks like you used an attribute from generated metadata containing more than one display form. Use one of the following display forms instead: " + identifiers.join(", ") + ".");
            }
            var keys = intersection_1.default(AGGREGATION_KEYS, Object.keys(i));
            if (!isEmpty_1.default(keys)) {
                var identifier = getIdentifier(i[keys[0]]);
                ts_invariant_1.default(false, contentErrorMessage + " It looks like you used an object " + identifier + " from generated metadata. You need to use one of the following aggregation functions instead: " + keys.join(", ") + ".");
            }
            ts_invariant_1.default(false, contentErrorMessage + " Got unknown content object: " + json_stable_stringify_1.default(i) + ".");
        }
        else {
            ts_invariant_1.default(false, contentErrorMessage + " Got unsupported content of type " + typeof i + ": " + i + ".");
        }
    });
    var totalsProp = !isEmpty_1.default(totals) ? { totals: totals } : {};
    return tslib_1.__assign({ localIdentifier: localId, items: items }, totalsProp);
}
exports.newBucket = newBucket;
/**
 * Tests whether the provided bucket is empty = contains no items and no totals.
 *
 * @param bucket - bucket to test
 * @returns true if empty, false if not
 * @public
 */
function bucketIsEmpty(bucket) {
    ts_invariant_1.default(bucket, "bucket must be specified");
    return bucket.items.length === 0 && (!bucket.totals || bucket.totals.length === 0);
}
exports.bucketIsEmpty = bucketIsEmpty;
/**
 * Gets the index of the first attribute matching the provided predicate from the bucket.
 *
 * If no predicate is provided, then the function defaults to anyAttribute predicate - meaning first found attribute
 * will be returned.
 *
 * This function also provides convenience to find attribute by its local identifier - if you pass predicate as
 * string the function will automatically create idMatchAttribute predicate.
 *
 * @param bucket - bucket to to search in
 * @param idOrFun - attribute identifier or instance of AttributePredicate; {@link anyAttribute} predicate is default
 * @returns -1 if no matching attribute is found
 * @public
 */
function bucketAttributeIndex(bucket, idOrFun) {
    if (idOrFun === void 0) { idOrFun = attribute_1.anyAttribute; }
    ts_invariant_1.default(bucket, "bucket must be specified");
    var predicate = typeof idOrFun === "string" ? attribute_1.idMatchAttribute(idOrFun) : idOrFun;
    var compositeGuard = function (obj) {
        return attribute_1.isAttribute(obj) && predicate(obj);
    };
    return findIndex_1.default(bucket.items, compositeGuard);
}
exports.bucketAttributeIndex = bucketAttributeIndex;
/**
 * Gets first attribute matching the provided predicate from the bucket.
 *
 * If no predicate is provided, then the function defaults to anyAttribute predicate - meaning first found attribute
 * will be returned.
 *
 * This function also provides convenience to find attribute by its local identifier - if you pass predicate as
 * string the function will automatically create idMatchAttribute predicate.
 *
 * @param bucket - bucket to to search in
 * @param idOrFun - attribute identifier or instance of AttributePredicate; {@link anyAttribute} predicate is default
 * @returns undefined if no matching attribute is found
 * @public
 */
function bucketAttribute(bucket, idOrFun) {
    if (idOrFun === void 0) { idOrFun = attribute_1.anyAttribute; }
    var index = bucketAttributeIndex(bucket, idOrFun);
    return index >= 0 ? bucket.items[index] : undefined;
}
exports.bucketAttribute = bucketAttribute;
/**
 * Gets all attributes matching the provided predicate from the bucket.
 *
 * If no predicate is provided, then the function defaults to anyAttribute predicate - meaning all attributes
 * from the bucket will be returned.
 *
 * @param bucket - bucket to work with
 * @param predicate - attribute predicate; {@link anyAttribute} predicate is default
 * @returns empty list if none match
 * @public
 */
function bucketAttributes(bucket, predicate) {
    if (predicate === void 0) { predicate = attribute_1.anyAttribute; }
    ts_invariant_1.default(bucket, "bucket must be specified");
    // need custom type-guard so as not to break type inference in filter() method
    var compositeGuard = function (obj) {
        return attribute_1.isAttribute(obj) && predicate(obj);
    };
    return bucket.items.filter(compositeGuard);
}
exports.bucketAttributes = bucketAttributes;
/**
 * Gets the index of the first measure matching the provided predicate from the bucket.
 *
 * If no predicate is provided, then the function defaults to anyMeasure predicate - meaning first found measure
 * will be returned.
 *
 * This function also provides convenience to find measure by its local identifier - if you pass predicate as
 * string the function will automatically create idMatchMeasure predicate.
 *
 * @param bucket - bucket to to search in
 * @param idOrFun - measure identifier or instance of MeasurePredicate; {@link anyMeasure} predicate is default
 * @returns -1 if no matching measure is found
 * @public
 */
function bucketMeasureIndex(bucket, idOrFun) {
    if (idOrFun === void 0) { idOrFun = measure_1.anyMeasure; }
    ts_invariant_1.default(bucket, "bucket must be specified");
    var predicate = typeof idOrFun === "string" ? measure_1.idMatchMeasure(idOrFun) : idOrFun;
    var compositeGuard = function (obj) {
        return measure_1.isMeasure(obj) && predicate(obj);
    };
    return findIndex_1.default(bucket.items, compositeGuard);
}
exports.bucketMeasureIndex = bucketMeasureIndex;
/**
 * Gets first measure matching the provided predicate from the bucket.
 *
 * If no predicate is provided, then the function defaults to anyMeasure predicate - meaning first found measure
 * will be returned.
 *
 * This function also provides convenience to find measure by its local identifier - if you pass predicate as
 * string the function will automatically create idMatchMeasure predicate.
 *
 * @param bucket - bucket to to search in
 * @param idOrFun - measure identifier or instance of MeasurePredicate; {@link anyMeasure} predicate is default
 * @returns undefined if no matching measure is found
 * @public
 */
function bucketMeasure(bucket, idOrFun) {
    if (idOrFun === void 0) { idOrFun = measure_1.anyMeasure; }
    var index = bucketMeasureIndex(bucket, idOrFun);
    return index >= 0 ? bucket.items[index] : undefined;
}
exports.bucketMeasure = bucketMeasure;
/**
 * Gets all measures matching the provided predicate from the bucket.
 *
 * If no predicate is provided, then the function defaults to anyMeasure predicate - meaning all measures from
 * the bucket will be returned.
 *
 * @param bucket - bucket to work with
 * @param predicate - measure predicate; {@link anyMeasure} predicate is default
 * @returns empty list if none match
 * @public
 */
function bucketMeasures(bucket, predicate) {
    if (predicate === void 0) { predicate = measure_1.anyMeasure; }
    ts_invariant_1.default(bucket, "bucket must be specified");
    // need custom type-guard so as not to break type inference in filter() method
    var compositeGuard = function (obj) {
        return measure_1.isMeasure(obj) && predicate(obj);
    };
    return bucket.items.filter(compositeGuard);
}
exports.bucketMeasures = bucketMeasures;
/**
 * Gets all attributes and measures from the bucket.
 *
 * @param bucket - bucket to work with
 * @returns empty list if no items
 * @public
 */
function bucketItems(bucket) {
    ts_invariant_1.default(bucket, "bucket must be specified");
    return bucket.items;
}
exports.bucketItems = bucketItems;
/**
 * Gets all totals from the bucket
 *
 * @param bucket - bucket to work with
 * @returns empty list if no totals
 * @public
 */
function bucketTotals(bucket) {
    ts_invariant_1.default(bucket, "bucket must be specified");
    if (!bucket.totals) {
        return [];
    }
    return bucket.totals;
}
exports.bucketTotals = bucketTotals;
/**
 * Gets a new bucket that 'inherits' all data from the provided bucket but has different totals. New
 * totals will be used in the new bucket as-is, no merging with existing totals.
 *
 * @param bucket - bucket to work with
 * @param totals - new totals to apply
 * @returns new bucket
 * @public
 */
function bucketSetTotals(bucket, totals) {
    if (totals === void 0) { totals = []; }
    ts_invariant_1.default(bucket, "bucket must be specified");
    return tslib_1.__assign(tslib_1.__assign({}, bucket), { totals: totals });
}
exports.bucketSetTotals = bucketSetTotals;
/**
 * Defines possible compute ratio sanitization rules.
 *
 * @public
 */
var ComputeRatioRule;
(function (ComputeRatioRule) {
    /**
     * Compute ratio must not be used in any measure
     */
    ComputeRatioRule[ComputeRatioRule["NEVER"] = 0] = "NEVER";
    /**
     * Compute ratio can be used if there is just a single measure
     */
    ComputeRatioRule[ComputeRatioRule["SINGLE_MEASURE_ONLY"] = 1] = "SINGLE_MEASURE_ONLY";
    /**
     * Compute ratio can be used on any measure
     */
    ComputeRatioRule[ComputeRatioRule["ANY_MEASURE"] = 2] = "ANY_MEASURE";
})(ComputeRatioRule = exports.ComputeRatioRule || (exports.ComputeRatioRule = {}));
/**
 * Applies compute ratio rule to all measures in a list - this MAY be done to sanitize measure definitions
 * so that the computed results make sense when visualized in a chart.
 *
 * The function will return a new list with updated measures according to the specified rule; see {@link ComputeRatioRule}.
 *
 * For convenience this function can work with list of measures AND attributes; attributes will be ignored
 * in processing and kept in resulting array as-is.
 *
 * @param items - list of attributes or measures to sanitize; attributes will be lef
 * @param rule - rule to apply; see {@link ComputeRatioRule}
 * @returns new list with modified measures; the original list and measures in it are left intact
 * @public
 */
function applyRatioRule(items, rule) {
    if (rule === void 0) { rule = ComputeRatioRule.SINGLE_MEASURE_ONLY; }
    ts_invariant_1.default(items, "items must be specified");
    if (rule === ComputeRatioRule.ANY_MEASURE) {
        return items;
    }
    var numberOfMeasures = items.filter(measure_1.isMeasure).length;
    if (numberOfMeasures > 1 || rule === ComputeRatioRule.NEVER) {
        return items.map(disableComputeRatio);
    }
    return items;
}
exports.applyRatioRule = applyRatioRule;
/**
 * Disables compute ratio if set on a simple measure. Does not do anything for other measures.
 *
 * @param item - maybe a simple measure where compute ratio should be disabled
 * @returns an instance of measure with compute ratio disabled
 * @public
 */
function disableComputeRatio(item) {
    if (measure_1.isSimpleMeasure(item)) {
        return factory_1.modifySimpleMeasure(item, function (m) { return m.noRatio(); });
    }
    return item;
}
exports.disableComputeRatio = disableComputeRatio;
/**
 * Creates a new bucket by modifying items of the provided input bucket.
 * Each item from the input bucket will be dispatched to the modification function
 * and the result of the modification will be included in the new bucket.
 *
 * Note: it is valid for the modification function to just return the original item.
 * In that case the item will be included in the bucket without modification.
 *
 * @param bucket - bucket in which all items are applied the modification function
 * @param modifications - the modification to apply to the bucket items
 * @returns new instance of bucket with modified bucket items
 * @public
 */
function bucketModifyItems(bucket, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    ts_invariant_1.default(bucket, "bucket must be specified");
    var items = bucketItems(bucket);
    return tslib_1.__assign(tslib_1.__assign({}, bucket), { items: items.map(function (bucketItem) { return modifications(bucketItem); }) });
}
exports.bucketModifyItems = bucketModifyItems;
/**
 * Creates a new bucket by modifying items of the provided input bucket.
 * Array of item from the input bucket will be dispatched to the reducer function
 * and the result of the modification will be included in the new bucket.
 *
 *
 * @param bucket - bucket in which all items are applied the modification function
 * @param reducer - the reducer function to apply to the bucket items
 * @returns new instance of bucket with modified bucket items
 * @public
 */
function bucketItemReduce(bucket, reducer) {
    if (reducer === void 0) { reducer = identity_1.default; }
    ts_invariant_1.default(bucket, "bucket must be specified");
    var items = bucketItems(bucket);
    var result = items.reduce(function (acc, cur, idx, src) {
        return reducer(acc, cur, idx, src);
    }, []);
    return tslib_1.__assign(tslib_1.__assign({}, bucket), { items: result });
}
exports.bucketItemReduce = bucketItemReduce;
//# sourceMappingURL=index.js.map